// A real, wired-up message row.
//
// This is the component the timeline uses in production — the
// reference-only renderer in `stories/placeholder-components.tsx` stays
// as the Storybook fixture for every message variant. Splitting the two
// keeps the visual spec readable while the production component takes
// on the richer concerns: hover-to-reveal actions, inline edit mode,
// delete confirmation, (edited) and [message deleted] placeholders.
//
// The row composes a list of `MessageAction`s through `MessageActions`
// and owns the popover state (emoji picker, more menu, delete dialog)
// here so every action's open/close travels with the row it belongs
// to. The builders in `components/message-actions/` produce the
// declarative shapes; this file wires them together.

import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from "react";

import { DeleteConfirmDialog } from "~/components/DeleteConfirmDialog";
import { EmojiPicker } from "~/components/EmojiPicker";
import { EncryptionBadge } from "~/components/EncryptionBadge";
import { MemberKindBadge } from "~/components/MemberKindBadge";
import { InlineEdit } from "~/components/InlineEdit";
import { LinkPreviewCard } from "~/components/LinkPreview";
import {
  MessageActions,
  buildDeleteAction,
  buildEditAction,
  buildMoreAction,
  buildQuoteAction,
  buildReactAction,
  buildReplyAction,
  type MessageAction,
} from "~/components/MessageActions";
import { MessageActionsBar } from "~/components/MessageActionsBar";
import { MoreMenu } from "~/components/MoreMenu";
import { ReportDialog } from "~/components/ReportDialog";
import { ReactionBar } from "~/components/ReactionBar";
import { ReadReceipts } from "~/components/ReadReceipts";
import { RequestKeysButton } from "~/components/RequestKeysButton";
import { SelectionToolbar } from "~/components/SelectionToolbar";
import { ThreadTail, useThreadUnread } from "~/components/mobile/ThreadTail";
import { ThreadPreview } from "~/components/ThreadPreview";
import { UserProfileCard } from "~/components/UserProfileCard";
import { useMessageActions } from "~/hooks/useMessageActions";
import {
  useAuthenticatedMedia,
  type AuthenticatedMediaThumbnail,
} from "~/hooks/useAuthenticatedMedia";
import { useThread } from "~/hooks/useThread";
import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import type {
  ActionResponse,
  Client,
  MessageEncryptionStatus,
  ReactionSummary,
  Receipt,
  TimelineEventStatus,
} from "~/lib/matrix";
import { canReportMessage } from "~/lib/matrix";
import { matrixLocalpart } from "~/lib/display-name";
import { getInitials } from "~/lib/initials";
import { MarkdownBody, type MessageMention } from "~/lib/markdown-render";
import { extractUrls, formatMessageTime } from "~/lib/message-utils";
import { parseNurionLink } from "~/lib/nurion-links";
import { insertQuote, type QuoteComposerHandle } from "~/lib/quote";
import type { EditRecallOrigin } from "~/hooks/useEditRecall";

// One document `selectionchange` listener shared across all rows, so a
// room with hundreds of bubbles keeps a single listener, not one each.
const selectionChangeHandlers = new Set<() => void>();
let selectionChangeListening = false;

function dispatchSelectionChange(): void {
  for (const handler of selectionChangeHandlers) handler();
}

function subscribeSelectionChange(handler: () => void): () => void {
  selectionChangeHandlers.add(handler);
  if (!selectionChangeListening) {
    document.addEventListener("selectionchange", dispatchSelectionChange);
    selectionChangeListening = true;
  }
  return () => {
    selectionChangeHandlers.delete(handler);
    if (selectionChangeHandlers.size === 0 && selectionChangeListening) {
      document.removeEventListener("selectionchange", dispatchSelectionChange);
      selectionChangeListening = false;
    }
  };
}

export type Density = "comfortable" | "compact";
export type MessageGroup = "first" | "middle" | "last" | "single";

export interface TimelineMessage {
  id: string;
  /** The id of the room this event belongs to. Consulted by
   *  `useMessageActions` to scope reactions / edits / deletes
   *  (omitted → `ready === false`, those buttons render disabled),
   *  and by the UTD "Request keys" action (omitted → the button is
   *  hidden via its own `canRequest` gate). Reply / More don't
   *  touch the SDK and stay enabled regardless. Production callers
   *  (`Timeline`) always set this; the optional marker is there for
   *  stories that mock a single message without wiring a room. */
  roomId?: string;
  sender: { id: string; name: string; avatarUrl: string | null };
  /** Pre-formatted timestamp. Grouping lives in the timeline layer. */
  time: string;
  /** Event's `origin_server_ts` (epoch ms). Threaded into
   *  `getUrlPreview` so Synapse's per-event preview cache keys two
   *  clients onto the same upstream fetch. */
  ts?: number;
  /** Text body, or (for a media row) the filename/caption fallback
   *  used by Quote and link-preview extraction. */
  body: string;
  /** When set, renders in place of `MessageTextBody` for a media row. */
  mediaBody?: ReactNode;
  /** User pills from the sender's `formatted_body`. */
  mentions?: MessageMention[];
  /** Bot notices read quieter; emotes (`/me`) read as actions. */
  variant?: "notice" | "emote";
  /** Unix ms of the last edit, or undefined for original messages. */
  editedAt?: number;
  /** True when the event has been redacted. */
  redacted?: boolean;
  reactions?: ReactionSummary[];
  content?: unknown;
  actionResponses?: ActionResponse[];
  group?: MessageGroup;
  /** Per-event encryption status. Omitted = unencrypted (no shield).
   *  Drives the per-message shield and the UTD placeholder body. */
  status?: MessageEncryptionStatus;
  /** Local delivery state. Sent rows stay quiet; sending/failed rows render subtle metadata. */
  deliveryStatus?: TimelineEventStatus;
  /** Upload progress of a media message still sending, 0–100. */
  uploadProgress?: number;
  /**
   * Read receipts attached to this event. The own-user receipt is
   * already excluded by `getReceiptsForEvent`; the bubble renders
   * the list as an avatar stack on the right edge.
   */
  receipts?: Receipt[];
  /**
   * Whether this message is the root of a thread that has at least
   * one reply. Derived upstream from the homeserver's bundled
   * `m.relations.m.thread` aggregation (`event.threadSummary`); the
   * bubble forwards this directly to `useThread`'s `enabled` flag so
   * non-root rows never subscribe. Missing (`undefined`) is treated
   * as `false` — the conservative default in production. Stories
   * that mock a single message without wiring a room can still
   * render the preview by passing `true` explicitly while supplying
   * a fixture summary through the mocked `useThread`.
   */
  isThreadRoot?: boolean;
  threadReplyCount?: number;
}

interface MessageBubbleProps {
  message: TimelineMessage;
  density?: Density;
  /**
   * Override for "is this my message?" — stories pass it explicitly so
   * the own / others' variants can render without a real login. In
   * production the component reads `useMatrix().userId` directly.
   */
  isOwnMessage?: boolean;
  /** Invoked when the user opens the thread panel on this message. */
  onOpenThread?: (messageId: string) => void;
  /**
   * Extra actions the embedder wants to inject (Save / Unsave is the
   * first — the Saved view builds a `buildSaveAction(...)` on the fly
   * and passes it here so the bar arranges it alongside the
   * ticket-native actions without MessageBubble knowing about Save).
   */
  extraActions?: MessageAction[];
  archivedReadOnly?: boolean;
  /**
   * Called when the user clicks "Request keys" on a UTD row. The
   * embedder (typically the Timeline container) wires this to
   * `requestKeysForEvent` — the bubble doesn't touch the SDK
   * directly. Resolving the promise means the request was sent; it
   * does not imply the keys arrived.
   */
  onRequestKeys?: (roomId: string, eventId: string) => Promise<void>;
  /**
   * Resolve a receipt user id to a display name for the avatar stack
   * tooltip. The embedder sources this from the SDK's member map. A
   * missing resolver falls back to the matrix-id localpart.
   */
  resolveReceiptName?: (userId: string) => string;
  /**
   * Resolve a receipt user id to an `mxc://` avatar URI for the read-
   * receipt pills. Mirrors `resolveReceiptName`; forwarded to
   * `ReadReceipts` as `resolveAvatar`. Missing / non-`mxc://` returns
   * fall back to initials.
   */
  resolveReceiptAvatar?: (userId: string) => string | null;
  quoteComposerRef?: RefObject<QuoteComposerHandle | null>;
  editing?: boolean;
  onEditingChange?: (next: boolean, origin?: EditRecallOrigin) => void;
  onRecallOlder?: () => void;
  onRecallNewer?: () => void;
  /**
   * Touch-first layout for the mobile shell: drops the avatar column
   * and the hover-action gutter (long-press opens the action sheet
   * instead) and stops the hover-reveal background. Reactions, thread
   * preview, receipts, and the body are unchanged — layout only.
   */
  mobile?: boolean;
  /**
   * Retry a failed send. When set and `deliveryStatus === "failed"`,
   * the row shows a tap-to-retry affordance beside the failed label.
   */
  onRetry?: () => void;
  /** Label for the `onRetry` affordance. Defaults to "Tap to retry". */
  retryLabel?: string;
}

export function avatarSizeFor(density: Density): number {
  return density === "compact" ? 24 : 32;
}

export function avatarThumbnail(size: number): AuthenticatedMediaThumbnail {
  return { w: size * 2, h: size * 2, method: "crop" };
}

function MessageAvatar({
  client,
  avatarUrl,
  name,
  size,
  onOpenProfile,
}: {
  client: Client | null;
  avatarUrl: string | null;
  name: string;
  size: number;
  onOpenProfile: (anchor: HTMLElement) => void;
}) {
  const mxc = avatarUrl?.startsWith("mxc://") ? avatarUrl : null;
  const media = useAuthenticatedMedia(client, mxc, {
    reuseAvatar: true,
    thumbnail: avatarThumbnail(size),
  });
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const src = media.src && media.src !== failedSrc ? media.src : null;
  const commonStyle = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
    flexShrink: 0,
    overflow: "hidden",
    borderRadius: "var(--chat-radius-md)",
  };
  return (
    <button
      type="button"
      aria-label={`Open profile for ${name}`}
      onClick={(event) => onOpenProfile(event.currentTarget)}
      className="chat-button"
      style={{
        display: "inline-flex",
        position: "relative",
        alignItems: "center",
        justifyContent: "center",
        ...commonStyle,
        border: "none",
        background: "var(--chat-surface-raised)",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontWeight: 300,
        fontSize: Math.max(10, Math.round(size * 0.4)),
        cursor: "pointer",
        padding: 0,
      }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          onError={() => setFailedSrc(src)}
          style={{ ...commonStyle, objectFit: "cover" }}
        />
      ) : (
        getInitials(name)
      )}
    </button>
  );
}

export function MessageBubble({
  message,
  density = "comfortable",
  isOwnMessage: isOwnOverride,
  onOpenThread,
  extraActions = [],
  archivedReadOnly = false,
  onRequestKeys,
  resolveReceiptName,
  resolveReceiptAvatar,
  quoteComposerRef,
  editing: controlledEditing,
  onEditingChange,
  onRecallOlder,
  onRecallNewer,
  mobile = false,
  onRetry,
  retryLabel,
}: MessageBubbleProps) {
  const { userId, client } = useMatrix();
  const rooms = useMatrixRooms();
  // `message.roomId` is optional on `TimelineMessage` — non-encrypted
  // bubbles can omit it. The hook treats `null` as "session not ready"
  // and rejects writes; that's the right contract for a bubble that
  // doesn't know its own room id (e.g. a story that mocks a single
  // message without wiring a room).
  const { toggleReaction, editMessage, deleteMessage, ready } = useMessageActions(
    message.roomId ?? null,
  );
  // Gate the thread subscription on the upstream "is this a thread
  // root?" signal so non-root rows never subscribe at all (#2653).
  // `isThreadRoot` is derived in `Timeline` from the SDK-bundled
  // `event.threadSummary?.replyCount > 0`; missing flag (e.g. a
  // story that mocks a single message without wiring a room) falls
  // back to `false` so we don't silently subscribe — the rule is
  // "no signal → no subscription". `useThread` already short-
  // circuits on `enabled === false` and on empty `roomId`.
  const { summary: loadedThreadSummary } = useThread(
    message.roomId ?? "",
    message.id,
    message.isThreadRoot ?? false,
  );

  const threadSummary = {
    ...loadedThreadSummary,
    replyCount: Math.max(message.threadReplyCount ?? 0, loadedThreadSummary.replyCount),
  };
  const threadUnread = useThreadUnread(message.id);

  const isOwnMessage = isOwnOverride ?? userId === message.sender.id;
  const isUtd = message.status === "undecryptable";
  // `revealed` drives the visibility of the action gutter. We sync
  // both pointer hover *and* keyboard focus-within into the same flag
  // so keyboard users can't tab into invisible, click-through buttons.
  const [revealed, setRevealed] = useState(false);
  const [localEditing, setLocalEditing] = useState(false);
  const editing = controlledEditing ?? localEditing;
  const setEditing = useCallback(
    (next: boolean, origin?: EditRecallOrigin) => {
      if (controlledEditing === undefined) setLocalEditing(next);
      onEditingChange?.(next, origin);
    },
    [controlledEditing, onEditingChange],
  );
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [selectionToolbar, setSelectionToolbar] = useState<{
    text: string;
    left: number;
    top: number;
    flipped: boolean;
  } | null>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const reactButtonRef = useRef<HTMLButtonElement | null>(null);
  const moreButtonRef = useRef<HTMLButtonElement | null>(null);
  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);

  const quotePayload = useCallback(
    (body: string) => ({
      roomId: message.roomId ?? "",
      sender: message.sender.name,
      ts: message.ts,
      eventId: message.id,
      body,
    }),
    [message.id, message.roomId, message.sender.name, message.ts],
  );

  const quoteMessage = useCallback(() => {
    if (!message.roomId) return;
    insertQuote(quoteComposerRef, quotePayload(message.body));
  }, [message.body, message.roomId, quoteComposerRef, quotePayload]);

  const updateSelectionToolbar = useCallback(() => {
    const row = rowRef.current;
    const body = bodyRef.current;
    const selection = document.getSelection();
    if (!row || !body || !selection || selection.rangeCount === 0 || selection.isCollapsed) {
      setSelectionToolbar(null);
      return;
    }
    const selectedText = selection.toString().trim();
    if (selectedText.length < 3) {
      setSelectionToolbar(null);
      return;
    }
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const selectionNode = container.nodeType === Node.TEXT_NODE ? container.parentNode : container;
    if (!selectionNode || !body.contains(selectionNode)) {
      setSelectionToolbar(null);
      return;
    }
    const rect = range.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      setSelectionToolbar(null);
      return;
    }
    const naturalTop = rect.top - rowRect.top - 8;
    const flipped = rect.top < 40;
    setSelectionToolbar({
      text: selectedText,
      left: rect.left - rowRect.left + rect.width / 2,
      top: flipped ? rect.bottom - rowRect.top + 8 : naturalTop,
      flipped,
    });
  }, []);

  useEffect(() => subscribeSelectionChange(updateSelectionToolbar), [updateSelectionToolbar]);

  const showAvatar = message.group === "first" || message.group === "single" || !message.group;
  // Silent for verified / unencrypted — the shield is reserved for
  // signal, not decoration. Header-row placement wins when the sender
  // header renders; grouped rows with no header keep the leading-body
  // placement so mid-group unverified messages still carry the signal.
  const shieldStatus =
    message.status && message.status !== "verified" && message.status !== "unencrypted"
      ? message.status
      : null;
  const avatarSize = avatarSizeFor(density);
  const avatarCol =
    density === "compact"
      ? "var(--chat-timeline-avatar-column-compact)"
      : "var(--chat-timeline-avatar-column-comfortable)";
  const groupGap = showAvatar
    ? "var(--chat-timeline-group-gap)"
    : "var(--chat-timeline-within-group-gap)";

  // Compose the action list. Order mirrors the brief's "React, Reply
  // in thread, Edit, Delete, More" layout; Edit and Delete only land
  // when the signed-in user is the sender; extra actions from the
  // embedder slot in after the built-ins so the overflow `More` stays
  // rightmost.
  // Actions that hit the Matrix wrapper (React, Edit, Delete) are
  // disabled until `ready` — `useMessageActions` now throws on the
  // unready path rather than silently no-op, so gating here is how
  // the UI honours that contract. Reply (delegates to #1301) and More
  // (client-side menu) stay enabled because they don't touch the SDK.
  // UTD rows expose no actions: react / reply / edit / delete are all
  // meaningless on a body we can't read. The "Request keys" button is
  // rendered inline by `UtdPlaceholder` — that's the single action the
  // user needs. Redacted rows follow the same rule.
  const actions: MessageAction[] =
    message.redacted || isUtd
      ? []
      : [
          buildReactAction({
            pickerOpen,
            onToggle: () => setPickerOpen((v) => !v),
            buttonRef: reactButtonRef,
            disabled: !ready || archivedReadOnly,
          }),
          buildReplyAction({ onClick: () => onOpenThread?.(message.id) }),
          buildQuoteAction({ onClick: quoteMessage }),
          ...(isOwnMessage
            ? [
                buildEditAction({
                  onClick: () => setEditing(true, "manual"),
                  disabled: !ready || archivedReadOnly || message.mediaBody !== undefined,
                }),
                buildDeleteAction({
                  onClick: () => setConfirmingDelete(true),
                  disabled: !ready || archivedReadOnly,
                }),
              ]
            : []),
          ...extraActions,
          buildMoreAction({
            menuOpen: moreOpen,
            onToggle: () => setMoreOpen((v) => !v),
            buttonRef: moreButtonRef,
          }),
        ];

  // A popover anchored to this row latches the action gutter open —
  // without this, opening the emoji picker (or the More menu) moves
  // focus out of the row, fires `onBlur`, and flips the gutter back
  // to `visibility: hidden` while the trigger still reports
  // `aria-expanded="true"`. The net result: a disembodied popover
  // with no visible anchor. Keeping `revealed` true while any
  // popover owns focus is the cheapest correct fix.
  const popoverOpen = pickerOpen || moreOpen || confirmingDelete;
  const gutterVisible = !mobile && (revealed || popoverOpen) && !editing && !message.redacted;

  return (
    <div
      ref={rowRef}
      data-event-id={message.id}
      onMouseEnter={() => setRevealed(true)}
      onMouseLeave={() => setRevealed(false)}
      onFocus={() => setRevealed(true)}
      onBlur={(e) => {
        // `relatedTarget` is the element receiving focus. If focus
        // stays inside the row, keep the gutter revealed; only when
        // focus leaves the row do we dismiss it.
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setRevealed(false);
      }}
      style={{
        position: "relative",
        display: "grid",
        gridTemplateColumns: mobile
          ? `${avatarCol} 1fr`
          : `${avatarCol} 1fr var(--chat-timeline-hover-action-gutter)`,
        gap: density === "compact" ? "var(--chat-space-2)" : "var(--chat-space-3)",
        padding: `${groupGap} var(--chat-space-4) 0 var(--chat-space-4)`,
        background:
          !mobile && (revealed || popoverOpen) && !message.redacted
            ? "var(--chat-surface-raised)"
            : "transparent",
        transition: "background var(--chat-motion-fast) var(--chat-motion-ease)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 2 }}>
        {showAvatar ? (
          <MessageAvatar
            client={client}
            avatarUrl={message.sender.avatarUrl}
            name={message.sender.name}
            size={avatarSize}
            onOpenProfile={setProfileAnchor}
          />
        ) : shieldStatus && !isUtd ? (
          // A grouped row's shield lives in the empty avatar gutter,
          // centred on the first line, so every body keeps one edge.
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: mobile ? TOUCH_BODY_LINE : "var(--chat-type-body-lg-line)",
              marginTop: -2,
            }}
          >
            <EncryptionBadge variant="message" status={shieldStatus} />
          </span>
        ) : null}
      </div>
      <div data-feedback-redact="true" style={{ minWidth: 0, position: "relative" }}>
        {message.deliveryStatus === "sending" && <SendingBar progress={message.uploadProgress} />}
        {showAvatar && (
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: "var(--chat-space-2)",
            }}
          >
            <button
              type="button"
              aria-label={`Open profile for ${message.sender.name}`}
              onClick={(event) => setProfileAnchor(event.currentTarget)}
              className="chat-button"
              style={{
                color: "hsl(var(--foreground) / 0.7)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: SENDER_LINE_SIZE,
                lineHeight: "var(--chat-type-body-xs-line)",
                fontWeight: 300,
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
              }}
            >
              {message.sender.name}
            </button>
            <MemberKindBadge userId={message.sender.id} />
            {!mobile && (
              <span
                style={{
                  color: "var(--chat-fg-muted)",
                  fontFamily: "var(--chat-font-sans)",
                  fontSize: "var(--chat-type-body-xs-size)",
                  lineHeight: "var(--chat-type-body-sm-line)",
                }}
              >
                {message.time}
              </span>
            )}
            <DeliveryStatus
              status={message.deliveryStatus}
              onRetry={onRetry}
              retryLabel={retryLabel}
            />
            {shieldStatus && !isUtd && (
              <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
                <EncryptionBadge variant="message" status={shieldStatus} />
              </span>
            )}
          </div>
        )}
        {message.redacted ? (
          <RedactedBody />
        ) : isUtd ? (
          <UtdPlaceholder
            roomId={message.roomId}
            eventId={message.id}
            onRequestKeys={onRequestKeys}
          />
        ) : message.mediaBody !== undefined ? (
          <>
            {message.mediaBody}
            {!showAvatar && (
              <DeliveryStatus
                status={message.deliveryStatus}
                onRetry={onRetry}
                retryLabel={retryLabel}
              />
            )}
          </>
        ) : editing ? (
          <InlineEdit
            initialBody={message.body}
            onCancel={() => setEditing(false)}
            onRecallOlder={onRecallOlder}
            onRecallNewer={onRecallNewer}
            onSave={async (next) => {
              await editMessage(message.id, next);
              setEditing(false);
            }}
          />
        ) : (
          <>
            <MessageTextBody
              mobile={mobile}
              body={message.body}
              mentions={message.mentions}
              variant={message.variant}
              resolveName={resolveReceiptName ?? defaultResolveName}
              edited={message.editedAt !== undefined}
              bodyRef={bodyRef}
            />
            {!showAvatar && (
              <DeliveryStatus
                status={message.deliveryStatus}
                onRetry={onRetry}
                retryLabel={retryLabel}
              />
            )}
          </>
        )}
        {!message.redacted && !isUtd && !editing && message.mediaBody === undefined && (
          <LinkPreviews body={message.body} client={client} ts={message.ts} />
        )}
        {!message.redacted && !isUtd && !editing && message.roomId && (
          <MessageActionsBar
            roomId={message.roomId}
            eventId={message.id}
            senderId={message.sender.id}
            content={message.content}
            responses={message.actionResponses}
            disabled={archivedReadOnly}
          />
        )}
        {!message.redacted && !editing && message.reactions && message.reactions.length > 0 && (
          <ReactionBar
            reactions={message.reactions}
            disabled={!ready}
            client={client}
            roomId={message.roomId ?? null}
            onToggle={(emoji) => toggleReaction(message.id, emoji, message.reactions ?? [])}
          />
        )}
        {/*
          Inline thread preview. Only renders when this message is the
          root of a thread that has at least one reply — empty / boot
          / non-root cases short-circuit on `replyCount === 0`. The
          `onOpen` reaches up through the same `onOpenThread(messageId)`
          seam the hover-action "Reply" button uses, so the embedder
          (Timeline → root shell) only has to wire one callback.
        */}
        {!message.redacted && !isUtd && !editing && threadSummary.replyCount > 0 && mobile && (
          <ThreadTail
            summary={threadSummary}
            client={client}
            unread={threadUnread}
            onOpen={() => onOpenThread?.(message.id)}
          />
        )}
        {!message.redacted && !isUtd && !editing && threadSummary.replyCount > 0 && !mobile && (
          <ThreadPreview
            summary={threadSummary}
            client={client}
            resolveSenderName={resolveReceiptName ?? defaultResolveName}
            lastReplyRelativeTime={
              threadSummary.lastReply ? formatMessageTime(threadSummary.lastReply.ts) : ""
            }
            hasUnread={false}
            onOpen={() => onOpenThread?.(message.id)}
          />
        )}
        {!message.redacted && !editing && message.receipts && message.receipts.length > 0 && (
          // Right-aligned avatar stack. Sits below the body inside the
          // body column so it travels with the message and never
          // collides with the hover-action gutter (which occupies its
          // own grid column). 8px right inset comes from the stack
          // itself via its own `marginRight`; we only need the flex
          // wrapper here so the stack hugs the right edge of the row.
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 2,
            }}
          >
            <ReadReceipts
              receipts={message.receipts}
              resolveName={resolveReceiptName ?? defaultResolveName}
              resolveAvatar={resolveReceiptAvatar}
            />
          </div>
        )}
      </div>
      {/*
        The action gutter. Hidden behind opacity + visibility when the
        row is idle so mouse users see a clean timeline; visibility
        takes tabbable elements out of the focus ring as well, so
        keyboard users can't focus controls they can't see.
      */}
      {!mobile && (
        <div
          style={{
            position: "relative",
            opacity: gutterVisible ? 1 : 0,
            visibility: gutterVisible ? "visible" : "hidden",
            transition: "opacity var(--chat-motion-fast) var(--chat-motion-ease)",
          }}
        >
          {/* Floats over the row rather than sizing it: a one-line grouped
              message stays one line tall, so a run keeps even spacing. */}
          <div
            style={{
              position: "absolute",
              // Centred on the row's first line, whatever its height.
              top: "calc(var(--chat-type-body-lg-line) / 2)",
              transform: "translateY(-50%)",
              right: "var(--chat-space-2)",
              zIndex: 1,
            }}
          >
            {actions.length > 0 && <MessageActions actions={actions} visible={gutterVisible} />}
          </div>
        </div>
      )}
      {pickerOpen && !message.redacted && (
        <EmojiPicker
          anchor={reactButtonRef.current}
          onSelect={(emoji) => {
            setPickerOpen(false);
            void toggleReaction(message.id, emoji, message.reactions ?? []);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {moreOpen && !message.redacted && (
        <MoreMenu
          anchor={moreButtonRef.current}
          onClose={() => setMoreOpen(false)}
          onReport={
            message.roomId &&
            canReportMessage(message.sender.id, userId, {
              isDirect: rooms.find((room) => room.id === message.roomId)?.isDirect ?? false,
            })
              ? () => {
                  setMoreOpen(false);
                  setReporting(true);
                }
              : undefined
          }
        />
      )}
      {reporting && message.roomId && (
        <ReportDialog
          roomId={message.roomId}
          eventId={message.id}
          senderId={message.sender.id}
          senderName={message.sender.name || message.sender.id}
          onClose={() => setReporting(false)}
        />
      )}
      {confirmingDelete && (
        <DeleteConfirmDialog
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={async () => {
            await deleteMessage(message.id);
            setConfirmingDelete(false);
          }}
        />
      )}
      {!mobile &&
        selectionToolbar &&
        message.roomId &&
        !editing &&
        !message.redacted &&
        !isUtd &&
        message.mediaBody === undefined && (
          <SelectionToolbar
            left={selectionToolbar.left}
            top={selectionToolbar.top}
            flipped={selectionToolbar.flipped}
            onQuote={() => {
              insertQuote(quoteComposerRef, quotePayload(selectionToolbar.text));
              document.getSelection()?.removeAllRanges();
              setSelectionToolbar(null);
            }}
          />
        )}
      {profileAnchor ? (
        <UserProfileCard
          userId={message.sender.id}
          fallbackDisplayName={message.sender.name}
          anchor={profileAnchor}
          roomId={message.roomId ?? null}
          onClose={() => setProfileAnchor(null)}
        />
      ) : null}
    </div>
  );
}

function DeliveryStatus({
  status,
  onRetry,
  retryLabel = "Tap to retry",
}: {
  status?: TimelineEventStatus;
  onRetry?: () => void;
  retryLabel?: string;
}) {
  if (status !== "sending" && status !== "failed") return null;
  const labelStyle = {
    color: status === "failed" ? "var(--chat-state-danger)" : "var(--chat-fg-muted)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-xs-size)",
    lineHeight: "var(--chat-type-body-xs-line)",
  } as const;
  if (status === "failed" && onRetry) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--chat-space-2)" }}>
        <span style={labelStyle}>Not sent</span>
        <button
          type="button"
          onClick={onRetry}
          className="chat-button"
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            padding: 0,
            color: "var(--chat-state-danger)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-xs-size)",
            lineHeight: "var(--chat-type-body-xs-line)",
            textDecoration: "underline",
          }}
        >
          {retryLabel}
        </button>
      </span>
    );
  }
  if (status === "failed") return <span style={labelStyle}>Not sent</span>;
  // In flight: announced, not drawn inline, so the row keeps its height;
  // `SendingBar` shows progress under the body without taking space.
  return <span className="sr-only">Sending</span>;
}

/**
 * A hairline under a message that is still on its way. It sits outside
 * the row's flow, so the row does not change height when the send
 * settles, and it stays invisible for the first moment so an immediate
 * send never flashes it.
 */
function SendingBar({ progress }: { progress?: number }) {
  if (progress === undefined) {
    return (
      <span aria-hidden className="chat-sending-bar">
        <span />
      </span>
    );
  }
  return (
    <span aria-hidden className="chat-sending-bar chat-sending-bar-progress">
      <span style={{ transform: `scaleX(${Math.min(100, Math.max(0, progress)) / 100})` }} />
    </span>
  );
}

/**
 * Render up to `MAX_LINK_PREVIEWS` link-preview cards under a message
 * body. Off-screen messages never mount a card — each
 * `LinkPreviewCard` gates on viewport visibility itself, so this
 * component's only job is to run `extractUrls` and lay the cards out.
 *
 * 8px top margin from the body, 4px gap between stacked cards, 16px
 * left inset from the body column — all owned by each
 * `LinkPreviewCard`'s inner wrapper so a message whose cards all fail
 * (renders nothing) leaves no reserved spacer behind it.
 */
function LinkPreviews({ body, client, ts }: { body: string; client: Client | null; ts?: number }) {
  const urls = extractUrls(body).filter((url) => parseNurionLink(url) === null);
  if (urls.length === 0) return null;
  return (
    <>
      {urls.map((url) => (
        <LinkPreviewCard key={url} client={client} url={url} ts={ts} />
      ))}
    </>
  );
}

/**
 * Fallback receipt name resolver. Extracts the Matrix localpart
 * (`@alice:server` → `alice`) when the embedder doesn't provide a
 * resolver. Good enough for a tooltip — production callers always
 * pass a real resolver that reads the SDK's display-name map.
 */
function defaultResolveName(userId: string): string {
  return matrixLocalpart(userId);
}

// Touch reads at 16/22: body 16px on screen, set a little tight so a message holds together as one block.
const TOUCH_BODY_SIZE = "16px";
const SENDER_LINE_SIZE = "11px";
const TOUCH_BODY_LINE = "22px";

function MessageTextBody({
  mobile,
  body,
  mentions,
  variant,
  resolveName,
  edited,
  bodyRef,
}: {
  mobile: boolean;
  body: string;
  mentions?: MessageMention[];
  variant?: "notice" | "emote";
  resolveName: (userId: string) => string;
  edited: boolean;
  /** Shield status to render before the body, or null. Set only for
   *  grouped rows on mobile, which have no avatar gutter to carry it —
   *  the header row or the gutter owns the shield everywhere else. */
  leadingShieldStatus?: MessageEncryptionStatus | null;
  bodyRef?: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      data-message-body=""
      style={{
        color: variant === "notice" ? "var(--chat-fg-secondary)" : "var(--chat-fg-primary)",
        fontStyle: variant === "emote" ? "italic" : undefined,
        fontFamily: "var(--chat-font-sans)",
        fontSize: mobile ? TOUCH_BODY_SIZE : "var(--chat-type-body-lg-size)",
        lineHeight: mobile ? TOUCH_BODY_LINE : "var(--chat-type-body-lg-line)",
        fontWeight: 300,
        margin: 0,
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--chat-space-2)",
        wordBreak: "break-word",
      }}
    >
      {/* Flex items default to min-width:auto, which can defeat wrapping for long tokens. */}
      <div ref={bodyRef} style={{ minWidth: 0, flex: 1 }}>
        <MarkdownBody
          text={body}
          mentions={mentions}
          resolveName={resolveName}
          trailing={
            edited ? (
              <span
                aria-label="edited"
                style={{
                  marginLeft: 4,
                  color: "var(--chat-fg-muted)",
                  fontSize: "var(--chat-type-body-xs-size)",
                  lineHeight: "var(--chat-type-body-xs-line)",
                }}
              >
                (edited)
              </span>
            ) : null
          }
        />
      </div>
    </div>
  );
}

function RedactedBody() {
  return (
    <p
      style={{
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-lg-size)",
        lineHeight: "var(--chat-type-body-lg-line)",
        fontStyle: "italic",
        margin: 0,
      }}
    >
      [message deleted]
    </p>
  );
}

/**
 * UTD placeholder — replaces the body when the event failed to
 * decrypt. The keys-request button + state machine lives in the
 * shared `RequestKeysButton` component, also used by the media-row
 * `AttachmentUnavailable` chip.
 */
function UtdPlaceholder({
  roomId,
  eventId,
  onRequestKeys,
}: {
  roomId?: string;
  eventId: string;
  onRequestKeys?: (roomId: string, eventId: string) => Promise<void>;
}) {
  const canRequest = !!onRequestKeys && !!roomId;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
      }}
    >
      <span style={{ flexShrink: 0 }}>
        <EncryptionBadge variant="message" status="undecryptable" />
      </span>
      <span
        style={{
          color: "var(--chat-fg-muted)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-lg-size)",
          lineHeight: "var(--chat-type-body-lg-line)",
          fontStyle: "italic",
          flex: 1,
        }}
      >
        Unable to decrypt
      </span>
      {canRequest && roomId && onRequestKeys && (
        <RequestKeysButton onRequestKeys={() => onRequestKeys(roomId, eventId)} />
      )}
    </div>
  );
}
